// src/pages/PLCEditor.jsx
//
// Full-page PLC editor (opens in its own tab). Replaces the old PLCModal +
// MergeVarsModal. Key idea: variables already configured for a PLC come straight
// from the DB and are editable immediately — no fetch needed. A fetch from the
// live PLC is only required (on demand, cached) when you want to discover and
// add NEW variables.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
  getPLCOverview,
  adminCreatePLC,
  adminUpdatePLC,
  adminUpdateImportantVars,
  adminUpdateWritableVars,
  fetchPLCVariables,
  getVariableGroups,
  adminUpdateVariableGroups,
} from "../api";
import "./AdminPortfolio.css";
import "./PLCEditor.css";

const EMPTY_FORM = { name: "", location: "", url: "", owner_id: "", timezone: "Asia/Kolkata" };

const TIMEZONES = [
  { label: "India (Kolkata, UTC+5:30)",          value: "Asia/Kolkata" },
  { label: "UAE / Oman (UTC+4)",                  value: "Asia/Dubai" },
  { label: "Saudi Arabia / Kuwait (UTC+3)",        value: "Asia/Riyadh" },
  { label: "East Africa / Nairobi (UTC+3)",        value: "Africa/Nairobi" },
  { label: "Pakistan (UTC+5)",                     value: "Asia/Karachi" },
  { label: "Bangladesh (UTC+6)",                   value: "Asia/Dhaka" },
  { label: "Sri Lanka (UTC+5:30)",                 value: "Asia/Colombo" },
  { label: "Nepal (UTC+5:45)",                     value: "Asia/Kathmandu" },
  { label: "Singapore / Malaysia (UTC+8)",         value: "Asia/Singapore" },
  { label: "Indonesia / Jakarta (UTC+7)",          value: "Asia/Jakarta" },
  { label: "Thailand / Vietnam (UTC+7)",           value: "Asia/Bangkok" },
  { label: "China / Philippines (UTC+8)",          value: "Asia/Shanghai" },
  { label: "Japan / Korea (UTC+9)",                value: "Asia/Tokyo" },
  { label: "Australia / Sydney (UTC+10/11)",       value: "Australia/Sydney" },
  { label: "New Zealand (UTC+12/13)",              value: "Pacific/Auckland" },
  { label: "UK / Ireland (UTC+0/+1)",              value: "Europe/London" },
  { label: "Central Europe (UTC+1/+2)",            value: "Europe/Berlin" },
  { label: "Eastern Europe (UTC+2/+3)",            value: "Europe/Helsinki" },
  { label: "Moscow (UTC+3)",                       value: "Europe/Moscow" },
  { label: "US Eastern (UTC-5/-4)",                value: "America/New_York" },
  { label: "US Central (UTC-6/-5)",                value: "America/Chicago" },
  { label: "US Mountain (UTC-7/-6)",               value: "America/Denver" },
  { label: "US Pacific (UTC-8/-7)",                value: "America/Los_Angeles" },
  { label: "Brazil / São Paulo (UTC-3)",           value: "America/Sao_Paulo" },
  { label: "UTC",                                  value: "UTC" },
];

function plcFetchErrorMessage(e) {
  const msg = e?.message || String(e);
  if (msg.includes("timed out") || msg.includes("timeout")) return "Could not reach PLC: connection timed out.";
  if (msg.includes("ConnectionPool") || msg.includes("ConnectionError") || msg.includes("ECONNREFUSED")) return "Could not reach PLC: connection refused.";
  if (msg.includes("Name or service not known") || msg.includes("getaddrinfo")) return "Could not reach PLC: hostname could not be resolved.";
  if (msg.includes("Could not reach PLC")) return "Could not reach PLC: the device appears to be offline.";
  return "Could not reach PLC: the device appears to be offline.";
}

function blankVarCfg() {
  return {
    display_name: "", is_boolean: false, bool_false_label: "", bool_true_label: "",
    show_plot: true, show_table: false,
    is_writable: false, data_type: "float", min_value: "", max_value: "", unit: "",
  };
}

// Build the editable selectedVars map from saved important_vars + writable_vars.
function buildSelectedVars(plc) {
  const m = {};
  for (const iv of (plc?.important_vars || [])) {
    const key = typeof iv === "string" ? iv : iv.variable_name;
    const w = (plc?.writable_vars || []).find(x => x.variable_name === key);
    m[key] = {
      display_name: (typeof iv === "object" && iv.display_name) || "",
      is_boolean: (typeof iv === "object" && !!iv.is_boolean) || false,
      bool_false_label: (typeof iv === "object" && iv.bool_false_label) || "",
      bool_true_label: (typeof iv === "object" && iv.bool_true_label) || "",
      show_plot: !(typeof iv === "object" && iv.show_plot === false),
      show_table: typeof iv === "object" && iv.show_table === true,
      is_writable: !!w,
      data_type: w?.data_type || "float",
      min_value: w?.min_value ?? "",
      max_value: w?.max_value ?? "",
      unit: w?.unit || "",
    };
  }
  // Writable vars not already in important_vars
  for (const w of (plc?.writable_vars || [])) {
    if (!m[w.variable_name]) {
      m[w.variable_name] = {
        ...blankVarCfg(),
        display_name: w.display_name || "",
        is_writable: true,
        data_type: w.data_type || "float",
        min_value: w.min_value ?? "",
        max_value: w.max_value ?? "",
        unit: w.unit || "",
      };
    }
  }
  return m;
}

function displayModeOf(cfg) {
  const plot = cfg.show_plot !== false;
  const table = cfg.show_table === true;
  if (plot && table) return "both";
  if (table) return "table";
  return "plot";
}

// ── Single configured-variable row ───────────────────────────────────────────
function VarRow({ name, cfg, onUpdate, onSetMode, onRemove }) {
  return (
    <div className="plcm-var-row plcm-var-row-on plce-var-row">
      <div className="plce-var-head">
        <span className="plcm-var-name">{name}</span>
        <button type="button" className="plce-var-remove" onClick={() => onRemove(name)} title="Remove variable">
          Remove
        </button>
      </div>
      <div className="plcm-var-config">
        <div className="plcm-cfg-row">
          <div className="plcm-cfg-field">
            <span className="plcm-cfg-label">Display name</span>
            <input className="ap-input plcm-cfg-input" placeholder={name} value={cfg.display_name}
              onChange={e => onUpdate(name, "display_name", e.target.value)} />
          </div>
        </div>
        <div className="plcm-cfg-toggles">
          <label className="plcm-pill-toggle">
            <input type="checkbox" checked={cfg.is_writable} onChange={e => onUpdate(name, "is_writable", e.target.checked)} />
            <span className="plcm-pill-track"><span className="plcm-pill-thumb" /></span>
            <span className="plcm-pill-label">Writable</span>
          </label>
          <label className="plcm-pill-toggle">
            <input type="checkbox" checked={cfg.is_boolean} onChange={e => onUpdate(name, "is_boolean", e.target.checked)} />
            <span className="plcm-pill-track"><span className="plcm-pill-thumb" /></span>
            <span className="plcm-pill-label">Boolean</span>
          </label>
          <div className="plcm-seg-group">
            <span className="plcm-seg-caption">Display as</span>
            <div className="plcm-segmented" role="group">
              {[["plot", "Plot"], ["table", "Table"], ["both", "Both"]].map(([val, label]) => (
                <button key={val} type="button"
                  className={`plcm-seg-btn${displayModeOf(cfg) === val ? " plcm-seg-btn-on" : ""}`}
                  onClick={() => onSetMode(name, val)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {cfg.is_boolean && (
          <div className="plcm-cfg-row plcm-cfg-row-tight">
            <div className="plcm-cfg-field">
              <span className="plcm-cfg-label">Label for 0</span>
              <input className="ap-input plcm-cfg-input" placeholder="Off" value={cfg.bool_false_label}
                onChange={e => onUpdate(name, "bool_false_label", e.target.value)} />
            </div>
            <div className="plcm-cfg-field">
              <span className="plcm-cfg-label">Label for 1</span>
              <input className="ap-input plcm-cfg-input" placeholder="On" value={cfg.bool_true_label}
                onChange={e => onUpdate(name, "bool_true_label", e.target.value)} />
            </div>
          </div>
        )}
        {cfg.is_writable && (
          <div className="plcm-cfg-row plcm-cfg-row-spaced">
            <div className="plcm-cfg-field plcm-cfg-field-sm">
              <span className="plcm-cfg-label">Data type</span>
              <select className="ap-input plcm-cfg-input" value={cfg.data_type} onChange={e => onUpdate(name, "data_type", e.target.value)}>
                <option value="float">Float</option>
                <option value="int">Integer</option>
                <option value="bool">Boolean</option>
              </select>
            </div>
            {cfg.data_type !== "bool" && (
              <>
                <div className="plcm-cfg-field plcm-cfg-field-sm">
                  <span className="plcm-cfg-label">Min</span>
                  <input className="ap-input plcm-cfg-input" type="number" placeholder="—" value={cfg.min_value}
                    onChange={e => onUpdate(name, "min_value", e.target.value)} />
                </div>
                <div className="plcm-cfg-field plcm-cfg-field-sm">
                  <span className="plcm-cfg-label">Max</span>
                  <input className="ap-input plcm-cfg-input" type="number" placeholder="—" value={cfg.max_value}
                    onChange={e => onUpdate(name, "max_value", e.target.value)} />
                </div>
                <div className="plcm-cfg-field plcm-cfg-field-sm">
                  <span className="plcm-cfg-label">Unit</span>
                  <input className="ap-input plcm-cfg-input" placeholder="e.g. °C" value={cfg.unit}
                    onChange={e => onUpdate(name, "unit", e.target.value)} />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PLCEditor() {
  const { plcId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerParam = searchParams.get("owner");
  const isNew = !plcId;

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedVars, setSelectedVars] = useState({});
  const [groups, setGroups] = useState([]);
  const [dirty, setDirty] = useState(false);

  // Add-variables panel (fetch-on-demand, cached)
  const [addOpen, setAddOpen] = useState(false);
  const [fetchedVars, setFetchedVars] = useState([]);
  const [fetchingVars, setFetchingVars] = useState(false);
  const [fetchErr, setFetchErr] = useState(null);
  const [addSearch, setAddSearch] = useState("");

  // Configured-list filter
  const [varSearch, setVarSearch] = useState("");

  // Group builder
  const [groupSelection, setGroupSelection] = useState(new Set());
  const [newGroupName, setNewGroupName] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [savedTick, setSavedTick] = useState(0);

  const markDirty = useCallback(() => { setDirty(true); setSavedTick(0); }, []);

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setLoadErr(null);
      try {
        const overview = await getPLCOverview();
        if (!alive) return;
        setClients(overview.map(c => ({ id: c.id, username: c.username })));

        if (isNew) {
          setForm({ ...EMPTY_FORM, owner_id: ownerParam ? Number(ownerParam) : "" });
          setSelectedVars({});
          setGroups([]);
        } else {
          let found = null;
          for (const c of overview) {
            const p = (c.plcs || []).find(x => String(x.id) === String(plcId));
            if (p) { found = p; break; }
          }
          if (!found) { setLoadErr("PLC not found."); setLoading(false); return; }
          setForm({
            name: found.name || "",
            location: found.location || "",
            url: found.url || "",
            owner_id: found.owner_id || "",
            timezone: found.timezone || "Asia/Kolkata",
          });
          setSelectedVars(buildSelectedVars(found));
          try {
            const gr = await getVariableGroups(plcId);
            if (alive) setGroups(gr.groups || []);
          } catch { /* groups optional */ }
        }
      } catch (e) {
        if (alive) setLoadErr(e.message || "Failed to load PLC.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [plcId, isNew, ownerParam]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  // ── Form + var mutations ─────────────────────────────────────────────────────
  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); markDirty(); };

  const updateVar = useCallback((name, field, value) => {
    setSelectedVars(s => ({ ...s, [name]: { ...s[name], [field]: value } }));
    markDirty();
  }, [markDirty]);

  const setMode = useCallback((name, mode) => {
    const show_plot = mode === "plot" || mode === "both";
    const show_table = mode === "table" || mode === "both";
    setSelectedVars(s => ({ ...s, [name]: { ...s[name], show_plot, show_table } }));
    markDirty();
  }, [markDirty]);

  const removeVar = useCallback((name) => {
    setSelectedVars(s => { const n = { ...s }; delete n[name]; return n; });
    // Drop from any group; remove groups that fall below 2 members
    setGroups(gs => gs
      .map(g => ({ ...g, variable_names: g.variable_names.filter(v => v !== name) }))
      .filter(g => g.variable_names.length >= 2));
    markDirty();
  }, [markDirty]);

  const addVar = useCallback((name) => {
    setSelectedVars(s => (s[name] ? s : { ...s, [name]: blankVarCfg() }));
    markDirty();
  }, [markDirty]);

  // ── Fetch live variables (on demand) ─────────────────────────────────────────
  function isValidUrl(url) {
    try { const u = new URL(url); return u.protocol === "http:" || u.protocol === "https:"; }
    catch { return false; }
  }

  async function handleFetch() {
    setFetchingVars(true); setFetchErr(null);
    try {
      const result = await fetchPLCVariables(form.url);
      const variables = Array.isArray(result?.variables) ? result.variables : [];
      if (variables.length === 0) {
        setFetchErr("No variables returned. Check that the URL is reachable and the PLC is online.");
        setFetchedVars([]); return;
      }
      setFetchedVars(variables);
    } catch (e) { setFetchErr(plcFetchErrorMessage(e)); }
    finally { setFetchingVars(false); }
  }

  // ── Group builder ────────────────────────────────────────────────────────────
  const configuredNames = useMemo(() => Object.keys(selectedVars), [selectedVars]);
  const groupedVars = useMemo(() => new Set(groups.flatMap(g => g.variable_names)), [groups]);

  function toggleGroupSel(v) {
    setGroupSelection(s => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });
  }
  function addGroup() {
    if (groupSelection.size < 2) return;
    setGroups(g => [...g, { name: newGroupName.trim() || `Group ${g.length + 1}`, variable_names: [...groupSelection] }]);
    setGroupSelection(new Set());
    setNewGroupName("");
    markDirty();
  }
  function removeGroup(idx) { setGroups(g => g.filter((_, i) => i !== idx)); markDirty(); }
  function updateGroupName(idx, name) { setGroups(g => g.map((gr, i) => i === idx ? { ...gr, name } : gr)); markDirty(); }
  function removeVarFromGroup(groupIdx, varName) {
    setGroups(g => g.map((gr, i) => i !== groupIdx ? gr : { ...gr, variable_names: gr.variable_names.filter(v => v !== varName) })
      .filter(gr => gr.variable_names.length >= 2));
    markDirty();
  }

  // ── Save ─────────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!form.name.trim()) { setSaveErr("Name is required."); return; }
    if (!form.url.trim()) { setSaveErr("URL is required."); return; }
    if (!form.owner_id) { setSaveErr("Assign the PLC to a client."); return; }
    setSaving(true); setSaveErr(null);
    try {
      const data = { ...form, owner_id: Number(form.owner_id) };
      let id = plcId;
      if (isNew) { const created = await adminCreatePLC(data); id = created.id; }
      else { await adminUpdatePLC(plcId, data); }

      const importantList = Object.entries(selectedVars).map(([variable_name, cfg]) => ({
        variable_name,
        display_name: cfg.display_name || null,
        is_boolean: cfg.is_boolean || false,
        bool_false_label: cfg.bool_false_label || null,
        bool_true_label: cfg.bool_true_label || null,
        show_plot: cfg.show_plot !== false,
        show_table: cfg.show_table === true,
      }));
      await adminUpdateImportantVars(id, importantList);

      const writableList = Object.entries(selectedVars)
        .filter(([, cfg]) => cfg.is_writable)
        .map(([variable_name, cfg]) => ({
          variable_name,
          display_name: cfg.display_name || null,
          data_type: cfg.data_type,
          min_value: cfg.min_value !== "" ? Number(cfg.min_value) : null,
          max_value: cfg.max_value !== "" ? Number(cfg.max_value) : null,
          unit: cfg.unit || null,
        }));
      await adminUpdateWritableVars(id, writableList);

      // Sanitize groups against current configured vars
      const cleanGroups = groups
        .map(g => ({ ...g, variable_names: g.variable_names.filter(v => selectedVars[v]) }))
        .filter(g => g.variable_names.length >= 2);
      await adminUpdateVariableGroups(id, cleanGroups);

      setDirty(false);
      setSavedTick(Date.now());
      if (isNew) navigate(`/admin/plc/${id}/edit`, { replace: true });
    } catch (e) {
      setSaveErr(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  // ── Derived ──────────────────────────────────────────────────────────────────
  const filteredConfigured = useMemo(() => {
    const q = varSearch.trim().toLowerCase();
    return configuredNames
      .filter(n => !q || n.toLowerCase().includes(q) || (selectedVars[n].display_name || "").toLowerCase().includes(q))
      .sort((a, b) => a.localeCompare(b));
  }, [configuredNames, varSearch, selectedVars]);

  const availableToAdd = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    return fetchedVars.filter(v => !selectedVars[v] && (!q || v.toLowerCase().includes(q)));
  }, [fetchedVars, selectedVars, addSearch]);

  const ungroupedConfigured = configuredNames.filter(v => !groupedVars.has(v));

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="plce-page">
      <Navbar />
      <div className="plce-content">
        <div className="plce-header">
          <div className="plce-header-left">
            <button type="button" className="plce-back" onClick={() => navigate("/admin")}>← Admin</button>
            <h1 className="plce-title">{isNew ? "Add PLC" : (form.name || "Edit PLC")}</h1>
          </div>
          <div className="plce-header-actions">
            {dirty && <span className="plce-dirty">Unsaved changes</span>}
            {!dirty && savedTick > 0 && <span className="plce-saved">Saved ✓</span>}
            <button type="button" className="ap-btn ap-btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {saveErr && <div className="ap-error plce-banner">{saveErr}</div>}

        {loading ? (
          <div className="ap-loading">Loading…</div>
        ) : loadErr ? (
          <div className="ap-error">{loadErr}</div>
        ) : (
          <>
            {/* Details */}
            <section className="plce-card">
              <div className="plce-card-title">PLC Details</div>
              <div className="plce-grid2">
                <div className="plcm-field">
                  <label className="plcm-label">Name *</label>
                  <input className="ap-input plcm-input" value={form.name} onChange={e => setField("name", e.target.value)} placeholder="e.g. Chiller Unit 1" />
                </div>
                <div className="plcm-field">
                  <label className="plcm-label">Location</label>
                  <input className="ap-input plcm-input" value={form.location} onChange={e => setField("location", e.target.value)} placeholder="e.g. Kanpur" />
                </div>
                <div className="plcm-field">
                  <label className="plcm-label">Timezone</label>
                  <select className="ap-input plcm-input" value={form.timezone} onChange={e => setField("timezone", e.target.value)}>
                    {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </select>
                </div>
                <div className="plcm-field">
                  <label className="plcm-label">Assign to client *</label>
                  <select className="ap-input plcm-input" value={form.owner_id} onChange={e => setField("owner_id", e.target.value)}>
                    <option value="">— select client —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.username}</option>)}
                  </select>
                </div>
                <div className="plcm-field plce-field-wide">
                  <label className="plcm-label">URL *</label>
                  <input className="ap-input plcm-input" value={form.url}
                    onChange={e => { setField("url", e.target.value); setFetchedVars([]); setFetchErr(null); }}
                    placeholder="http://192.168.1.100" />
                  {form.url.trim() && !isValidUrl(form.url) && (
                    <div className="ap-url-hint">Enter a valid URL starting with http:// or https://</div>
                  )}
                </div>
              </div>
            </section>

            {/* Configured variables */}
            <section className="plce-card">
              <div className="plce-card-head">
                <div className="plce-card-title">
                  Configured Variables
                  <span className="plcm-selected-badge">{configuredNames.length}</span>
                </div>
                <div className="plce-card-head-actions">
                  {configuredNames.length > 0 && (
                    <input className="ap-input plce-search" placeholder="Filter…" value={varSearch} onChange={e => setVarSearch(e.target.value)} />
                  )}
                  <button type="button" className={`ap-btn ${addOpen ? "ap-btn-secondary" : "ap-btn-primary"}`} onClick={() => setAddOpen(o => !o)}>
                    {addOpen ? "Close" : "+ Add variables"}
                  </button>
                </div>
              </div>

              {/* Add panel — fetch on demand */}
              {addOpen && (
                <div className="plce-add-panel">
                  <div className="mvm-fetch-row">
                    <code className="mvm-url">{form.url || "Set a URL above first"}</code>
                    <button type="button" className="ap-btn ap-btn-secondary" disabled={!isValidUrl(form.url) || fetchingVars} onClick={handleFetch}>
                      {fetchingVars ? "Fetching…" : (fetchedVars.length > 0 ? "Re-fetch" : "Fetch Variables")}
                    </button>
                  </div>
                  {fetchErr && <div className="ap-error" style={{ marginTop: 8 }}>{fetchErr}</div>}
                  {fetchedVars.length > 0 && (
                    <>
                      <div className="plce-add-toolbar">
                        <span className="ap-muted">{availableToAdd.length} available to add</span>
                        <input className="ap-input plce-search" placeholder="Search variables…" value={addSearch} onChange={e => setAddSearch(e.target.value)} />
                      </div>
                      <div className="plce-add-grid">
                        {availableToAdd.length === 0
                          ? <span className="ap-muted">All fetched variables are already configured.</span>
                          : availableToAdd.map(v => (
                              <button key={v} type="button" className="plce-add-chip" onClick={() => addVar(v)} title={`Add ${v}`}>
                                <span className="plce-add-chip-plus">+</span>{v}
                              </button>
                            ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Configured list */}
              <div className="plcm-var-list plce-var-list">
                {configuredNames.length === 0 ? (
                  <div className="plcm-vars-empty">
                    No variables configured yet. Click <strong>+ Add variables</strong> to fetch from the PLC and pick some.
                  </div>
                ) : filteredConfigured.length === 0 ? (
                  <div className="plcm-vars-empty">No variables match your filter.</div>
                ) : (
                  filteredConfigured.map(name => (
                    <VarRow key={name} name={name} cfg={selectedVars[name]}
                      onUpdate={updateVar} onSetMode={setMode} onRemove={removeVar} />
                  ))
                )}
              </div>
            </section>

            {/* Merged plots / groups */}
            <section className="plce-card">
              <div className="plce-card-title">Merged Plots</div>
              <p className="plce-card-sub">Plot multiple variables together on one chart. Pick from your configured variables.</p>

              {groups.length > 0 && (
                <div className="plce-groups">
                  {groups.map((grp, i) => (
                    <div key={i} className="mvm-group-card">
                      <div className="mvm-group-card-header">
                        <input className="ap-input mvm-group-name-input" value={grp.name}
                          onChange={e => updateGroupName(i, e.target.value)} placeholder={`Group ${i + 1}`} />
                        <button type="button" className="ap-btn ap-btn-danger ap-btn-sm" onClick={() => removeGroup(i)}>Delete group</button>
                      </div>
                      <div className="mvm-group-vars">
                        {grp.variable_names.map(v => (
                          <span key={v} className="mvm-var-chip mvm-var-chip-grouped">
                            {v}
                            <button type="button" className="mvm-chip-remove" onClick={() => removeVarFromGroup(i, v)} title="Remove from group">×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {ungroupedConfigured.length >= 2 ? (
                <div className="plce-group-builder">
                  <div className="mvm-create-row">
                    <input className="ap-input mvm-group-name-input" placeholder="Group name (optional)" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
                    <button type="button" className="ap-btn ap-btn-primary" onClick={addGroup} disabled={groupSelection.size < 2}>
                      + Add Group {groupSelection.size >= 2 ? `(${groupSelection.size})` : ""}
                    </button>
                  </div>
                  <div className="plcm-pane-title" style={{ marginTop: 14 }}>Click variables to group them</div>
                  <div className="mvm-chips">
                    {ungroupedConfigured.map(v => (
                      <button key={v} type="button"
                        className={`mvm-chip${groupSelection.has(v) ? " mvm-chip-selected" : ""}`}
                        onClick={() => toggleGroupSel(v)}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="plcm-vars-empty">Configure at least two ungrouped variables to create a merged plot.</div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
